// Utility functions for converting email blocks to HTML and plain text
// All HTML output uses table-based layout with inline styles for email client compatibility

import type {
  EmailBlock,
  EmailBlockData,
  HeaderBlockData,
  TextBlockData,
  ImageBlockData,
  ButtonBlockData,
  DividerBlockData,
  SpacerBlockData,
  ColumnsBlockData,
  ColumnContent,
} from './types';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
}

function widthValue(width: 'full' | 'medium' | 'small'): string {
  switch (width) {
    case 'full':
      return '100%';
    case 'medium':
      return '400';
    case 'small':
      return '200';
  }
}

function alignmentTd(alignment: 'left' | 'center' | 'right'): string {
  return `align="${alignment}"`;
}

function headerBlockToHtml(data: HeaderBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${data.backgroundColor};border-radius:8px 8px 0 0;">
  <tr>
    <td style="padding:30px 20px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
      <h1 style="margin:0;font-size:24px;color:${data.textColor};font-weight:700;">${escapeHtml(data.organizationName)}</h1>
    </td>
  </tr>
</table>`;
}

function textBlockToHtml(data: TextBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="padding:16px 24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:16px;line-height:1.6;color:#333333;text-align:${data.alignment};">
      ${data.content}
    </td>
  </tr>
</table>`;
}

function imageBlockToHtml(data: ImageBlockData): string {
  if (!data.url) {
    return '';
  }
  const imgWidth = widthValue(data.width);
  const widthAttr = data.width === 'full' ? 'width="100%"' : `width="${imgWidth}"`;
  const imgTag = `<img src="${escapeHtml(data.url)}" alt="${escapeHtml(data.alt)}" ${widthAttr} style="display:block;max-width:100%;height:auto;border:0;" />`;
  const wrapped = data.linkUrl
    ? `<a href="${escapeHtml(data.linkUrl)}" target="_blank" style="text-decoration:none;">${imgTag}</a>`
    : imgTag;

  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td ${alignmentTd(data.alignment)} style="padding:16px 24px;">
      ${wrapped}
    </td>
  </tr>
</table>`;
}

function buttonBlockToHtml(data: ButtonBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td ${alignmentTd(data.alignment)} style="padding:16px 24px;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="background:${data.backgroundColor};border-radius:${data.borderRadius}px;padding:12px 28px;">
            <a href="${escapeHtml(data.url)}" target="_blank" style="color:${data.textColor};text-decoration:none;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:16px;font-weight:600;display:inline-block;">${escapeHtml(data.text)}</a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

function dividerBlockToHtml(data: DividerBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="padding:8px 24px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="border-bottom:${data.thickness}px ${data.style} ${data.color};font-size:1px;line-height:1px;">&nbsp;</td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

function spacerBlockToHtml(data: SpacerBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="height:${data.height}px;font-size:1px;line-height:1px;">&nbsp;</td>
  </tr>
</table>`;
}

function columnContentToHtml(col: ColumnContent): string {
  if (col.type === 'image' && col.url) {
    return `<img src="${escapeHtml(col.url)}" alt="${escapeHtml(col.alt || '')}" width="100%" style="display:block;max-width:100%;height:auto;border:0;" />`;
  }
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:16px;line-height:1.6;color:#333333;">${col.content}</div>`;
}

function columnsBlockToHtml(data: ColumnsBlockData): string {
  return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="padding:16px 24px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td width="50%" valign="top" style="padding-right:12px;">
            ${columnContentToHtml(data.left)}
          </td>
          <td width="50%" valign="top" style="padding-left:12px;">
            ${columnContentToHtml(data.right)}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

function blockToHtml(data: EmailBlockData): string {
  switch (data.type) {
    case 'header':
      return headerBlockToHtml(data);
    case 'text':
      return textBlockToHtml(data);
    case 'image':
      return imageBlockToHtml(data);
    case 'button':
      return buttonBlockToHtml(data);
    case 'divider':
      return dividerBlockToHtml(data);
    case 'spacer':
      return spacerBlockToHtml(data);
    case 'columns':
      return columnsBlockToHtml(data);
  }
}

export function emailBlocksToHtml(blocks: EmailBlock[], organizationName: string): string {
  const blocksHtml = blocks.map((block) => blockToHtml(block.data)).join('\n');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(organizationName)}</title>
</head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f3f4f6;">
    <tr>
      <td align="center" style="padding:20px 0;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;max-width:600px;width:100%;">
          ${blocksHtml}
        </table>
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;">
          <tr>
            <td style="padding:20px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:12px;color:#6b7280;">
              <p style="margin:0;">${escapeHtml(organizationName)}</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// Plain text conversion

function headerBlockToText(data: HeaderBlockData): string {
  return `${data.organizationName}\n${'='.repeat(data.organizationName.length)}`;
}

function textBlockToText(data: TextBlockData): string {
  return stripHtml(data.content);
}

function imageBlockToText(data: ImageBlockData): string {
  const parts: string[] = [];
  if (data.alt) parts.push(`[Image: ${data.alt}]`);
  if (data.linkUrl) parts.push(`Link: ${data.linkUrl}`);
  return parts.join('\n') || '[Image]';
}

function buttonBlockToText(data: ButtonBlockData): string {
  return `${data.text}: ${data.url}`;
}

function dividerBlockToText(): string {
  return '---';
}

function spacerBlockToText(): string {
  return '';
}

function columnContentToText(col: ColumnContent): string {
  if (col.type === 'image') {
    return col.alt ? `[Image: ${col.alt}]` : '[Image]';
  }
  return stripHtml(col.content);
}

function columnsBlockToText(data: ColumnsBlockData): string {
  const leftText = columnContentToText(data.left);
  const rightText = columnContentToText(data.right);
  return `${leftText}\n${rightText}`;
}

function blockToText(data: EmailBlockData): string {
  switch (data.type) {
    case 'header':
      return headerBlockToText(data);
    case 'text':
      return textBlockToText(data);
    case 'image':
      return imageBlockToText(data);
    case 'button':
      return buttonBlockToText(data);
    case 'divider':
      return dividerBlockToText();
    case 'spacer':
      return spacerBlockToText();
    case 'columns':
      return columnsBlockToText(data);
  }
}

export function emailBlocksToText(blocks: EmailBlock[]): string {
  return blocks
    .map((block) => blockToText(block.data))
    .filter(Boolean)
    .join('\n\n');
}

// Default block creation

let blockIdCounter = 0;

export function generateBlockId(): string {
  blockIdCounter += 1;
  return `block-${Date.now()}-${blockIdCounter}`;
}

export function createDefaultBlock(type: EmailBlockData['type'], organizationName?: string): EmailBlockData {
  switch (type) {
    case 'header':
      return {
        type: 'header',
        organizationName: organizationName || 'Organization Name',
        backgroundColor: 'linear-gradient(135deg, #1e40af, #3b82f6)',
        textColor: '#ffffff',
      };
    case 'text':
      return {
        type: 'text',
        content: '<p>Enter your text here...</p>',
        alignment: 'left',
      };
    case 'image':
      return {
        type: 'image',
        url: '',
        alt: '',
        width: 'full',
        alignment: 'center',
        linkUrl: '',
      };
    case 'button':
      return {
        type: 'button',
        text: 'Click Here',
        url: 'https://',
        backgroundColor: '#1e40af',
        textColor: '#ffffff',
        alignment: 'center',
        borderRadius: 6,
      };
    case 'divider':
      return {
        type: 'divider',
        style: 'solid',
        color: '#e5e7eb',
        thickness: 1,
      };
    case 'spacer':
      return {
        type: 'spacer',
        height: 24,
      };
    case 'columns':
      return {
        type: 'columns',
        left: { type: 'text', content: '<p>Left column</p>' },
        right: { type: 'text', content: '<p>Right column</p>' },
      };
  }
}
